import React, { useEffect } from "react";

import {
  useEditor,
  EditorContent,
} from "@tiptap/react";

import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import TextAlign from "@tiptap/extension-text-align";
import { CharacterCount } from "@tiptap/extensions";


const ProRichTextEditor = ({
  value = "",
  onChange,
  placeholder = "Escriba aquí...",
  maxLength = null,
}) => {

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,

      TextAlign.configure({
        types: ["heading", "paragraph"],
      }),
    ],

    content: value || "",

    editorProps: {
      attributes: {
        class: "pro-rich-editor-content",
      },

      // =====================================================
      // BLOQUEAR ESCRITURA AL LLEGAR AL MAXIMO
      // =====================================================

      handleTextInput: (view, from, to, text) => {

        if (!maxLength) {
          return false;
        }

        // Texto actual del editor
        const currentText = view.state.doc.textBetween(
          0,
          view.state.doc.content.size,
          "",
          ""
        );

        // Texto que está seleccionado y será reemplazado
        const selectedText = view.state.doc.textBetween(
          from,
          to,
          "",
          ""
        );

        const newLength =
          currentText.length -
          selectedText.length +
          text.length;

        // Si supera el límite, bloquear
        if (newLength > maxLength) {
          return true;
        }

        // Permitir escritura
        return false;
      },


      // =====================================================
      // BLOQUEAR PEGADO SI SUPERA EL MAXIMO
      // =====================================================

      handlePaste: (view, event) => {

        if (!maxLength) {
          return false;
        }

        const pastedText =
          event.clipboardData?.getData("text/plain") || "";

        const currentText = view.state.doc.textBetween(
          0,
          view.state.doc.content.size,
          "",
          ""
        );

        const { from, to } =
          view.state.selection;

        const selectedText =
          view.state.doc.textBetween(
            from,
            to,
            "",
            ""
          );

        const newLength =
          currentText.length -
          selectedText.length +
          pastedText.length;

        // Bloquear pegado si excede el límite
        if (newLength > maxLength) {
          return true;
        }

        return false;
      },
    },


    // =====================================================
    // ACTUALIZAR ESTADO
    // =====================================================

    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
  });


  // =====================================================
  // SINCRONIZAR DATOS DEL BACKEND
  // =====================================================

  useEffect(() => {

    if (!editor) return;

    const incomingValue =
      value || "";

    if (
      editor.getHTML() !== incomingValue
    ) {

      editor.commands.setContent(
        incomingValue,
        false
      );

    }

  }, [value, editor]);


  if (!editor) {
    return null;
  }

  return (
    <div className="pro-rich-editor">

      {/* ========================================= */}
      {/* TOOLBAR */}
      {/* ========================================= */}

      <div className="pro-rich-toolbar">

        <button
          type="button"
          className="pro-editor-btn"
          onClick={() =>
            editor
              .chain()
              .focus()
              .undo()
              .run()
          }
          title="Deshacer"
        >
          <i className="bi bi-arrow-counterclockwise"></i>
        </button>


        <button
          type="button"
          className="pro-editor-btn"
          onClick={() =>
            editor
              .chain()
              .focus()
              .redo()
              .run()
          }
          title="Rehacer"
        >
          <i className="bi bi-arrow-clockwise"></i>
        </button>


        <div className="pro-editor-divider"></div>


        {/* NEGRITA */}

        <button
          type="button"
          className={`pro-editor-btn ${
            editor.isActive("bold")
              ? "active"
              : ""
          }`}
          onClick={() =>
            editor
              .chain()
              .focus()
              .toggleBold()
              .run()
          }
          title="Negrita"
        >
          <i className="bi bi-type-bold"></i>
        </button>


        {/* CURSIVA */}

        <button
          type="button"
          className={`pro-editor-btn ${
            editor.isActive("italic")
              ? "active"
              : ""
          }`}
          onClick={() =>
            editor
              .chain()
              .focus()
              .toggleItalic()
              .run()
          }
          title="Cursiva"
        >
          <i className="bi bi-type-italic"></i>
        </button>


        {/* SUBRAYADO */}

        <button
          type="button"
          className={`pro-editor-btn ${
            editor.isActive("underline")
              ? "active"
              : ""
          }`}
          onClick={() =>
            editor
              .chain()
              .focus()
              .toggleUnderline()
              .run()
          }
          title="Subrayado"
        >
          <i className="bi bi-type-underline"></i>
        </button>


        <div className="pro-editor-divider"></div>


        {/* ALINEAR IZQUIERDA */}

        <button
          type="button"
          className={`pro-editor-btn ${
            editor.isActive({
              textAlign: "left",
            })
              ? "active"
              : ""
          }`}
          onClick={() =>
            editor
              .chain()
              .focus()
              .setTextAlign("left")
              .run()
          }
        >
          <i className="bi bi-text-left"></i>
        </button>


        {/* CENTRO */}

        <button
          type="button"
          className={`pro-editor-btn ${
            editor.isActive({
              textAlign: "center",
            })
              ? "active"
              : ""
          }`}
          onClick={() =>
            editor
              .chain()
              .focus()
              .setTextAlign("center")
              .run()
          }
        >
          <i className="bi bi-text-center"></i>
        </button>


        {/* DERECHA */}

        <button
          type="button"
          className={`pro-editor-btn ${
            editor.isActive({
              textAlign: "right",
            })
              ? "active"
              : ""
          }`}
          onClick={() =>
            editor
              .chain()
              .focus()
              .setTextAlign("right")
              .run()
          }
        >
          <i className="bi bi-text-right"></i>
        </button>


        <div className="pro-editor-divider"></div>


        {/* LISTA */}

        <button
          type="button"
          className={`pro-editor-btn ${
            editor.isActive("bulletList")
              ? "active"
              : ""
          }`}
          onClick={() =>
            editor
              .chain()
              .focus()
              .toggleBulletList()
              .run()
          }
        >
          <i className="bi bi-list-ul"></i>
        </button>


        {/* LISTA NUMERADA */}

        <button
          type="button"
          className={`pro-editor-btn ${
            editor.isActive("orderedList")
              ? "active"
              : ""
          }`}
          onClick={() =>
            editor
              .chain()
              .focus()
              .toggleOrderedList()
              .run()
          }
        >
          <i className="bi bi-list-ol"></i>
        </button>

      </div>


      {/* ========================================= */}
      {/* EDITOR */}
      {/* ========================================= */}

      <EditorContent
        editor={editor}
      />

    </div>
  );
};


export default ProRichTextEditor;